import {
  AlertTriangle,
  BookOpenText,
  Check,
  CheckCheck,
  ChevronRight,
  ChevronsRight,
  CircleHelp,
  CircleUser,
  GitFork,
  List,
  Sparkles,
  SpellCheck,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type Contradiction, type PendingCorrection, type ReviewInsight } from "@/lib/api";
import { cn } from "@/lib/cn";
import { withDate } from "@/lib/format";
import { NEEDS_YOU_COPY, useNeedsYou, type NeedsYouCard, type NeedsYouKind } from "@/lib/needsYou";
import { describeUngrounded } from "@/lib/ungrounded";
import { QuestionAnswer } from "@/components/QuestionAnswer";
import { SpeakerCard } from "./SpeakerCard";

/**
 * UI-16's **Needs you** — one stream, one card at a time, five kinds of card (UI-21 added the
 * system's own questions, last).
 *
 * The whole increment's rule is visible here: this is a second surface and never a second
 * pipeline. Every button on every card calls the endpoint full mode already calls, so a
 * card answered here is gone from the review queue, from Transcript Review or from the
 * recording's Speakers tab, and one answered there never appears here.
 *
 * **Nothing is decided with less information than full mode shows.** What simple mode drops
 * is the machinery — the category badge, the entity chips, the "How this was read"
 * disclosure, the confidence numbers — not the things that bear on whether the answer is
 * right. An insight the extractor could not ground still says so, in this surface's own
 * words, because approving it anyway is how a belief about nothing in particular gets into
 * the world view.
 *
 * **The vocabulary is deliberately not the system's.** No counts of anything but the
 * position in the queue, no tables, and none of the six verdict words: the copy carries the
 * meaning instead, which is why the primary button says "Add to world view" rather than
 * "Approve" and a belief pair is "Two beliefs disagree".
 */
export function SimpleNeedsYou() {
  const { cards, problem, answered, reload } = useNeedsYou();

  const [index, setIndex] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const [leaving, setLeaving] = useState<"left" | "right" | null>(null);

  const queue = cards ?? [];
  const total = queue.length;
  // Wraps, so skipping past the last card comes back to the first rather than emptying the
  // screen — "skip for now" means for now, not for ever.
  const position = total ? ((index % total) + total) % total : 0;
  const current = queue[position];

  // A queue that shrinks under the cursor must not leave the cursor past its end.
  useEffect(() => {
    if (total > 0 && index >= total) setIndex(0);
  }, [index, total]);

  function drop(card: NeedsYouCard, direction: "left" | "right") {
    setLeaving(direction);

    // A fixed number rather than a transition-end listener, because the card unmounts
    // either way and a listener on an element that is going away never fires.
    window.setTimeout(() => {
      setLeaving(null);
      answered(card.id);
    }, 220);
  }

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-12)]">
      <header className="flex flex-col gap-2.5">
        <h1 className="text-[40px] font-extrabold leading-[1.1] tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Needs you
        </h1>
        <p className="max-w-[54ch] text-[length:var(--text-lg)] leading-[1.6] text-[var(--text-secondary)] [text-wrap:pretty]">
          Only the things Factorium cannot decide on its own.
        </p>
      </header>

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {cards === null ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : total === 0 ? (
        <AllClear />
      ) : (
        <>
          <div className="flex items-center gap-[var(--space-3)]">
            <span className="text-[length:var(--text-xs)] tabular-nums text-[var(--text-muted)]">
              {position + 1} of {total}
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setListOpen((open) => !open)}
              aria-expanded={listOpen}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border border-[var(--border-subtle)] px-3 py-1 text-[length:var(--text-xs)] text-[var(--text-muted)] transition hover:border-[var(--border-strong)] hover:text-[var(--text-secondary)]"
            >
              <List size={12} strokeWidth={2} />
              {listOpen ? "One at a time" : `See all ${total}`}
            </button>
          </div>

          {listOpen ? (
            <ul className="flex flex-col overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border-subtle)]">
              {queue.map((card, at) => (
                <li key={card.id}>
                  <QueueRow
                    card={card}
                    current={at === position}
                    onOpen={() => {
                      setIndex(at);
                      setListOpen(false);
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            current && (
              <article
                key={current.id}
                className={cn(
                  "flex flex-col gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] shadow-[var(--shadow-sm)] backdrop-blur-[var(--blur-sm)]",
                  leaving === "right" && "[animation:omExitRight_220ms_cubic-bezier(0.4,0,0.2,1)_both]",
                  leaving === "left" && "[animation:omExitLeft_220ms_cubic-bezier(0.4,0,0.2,1)_both]",
                )}
              >
                <div className="flex flex-wrap items-center gap-2.5">
                  <KindBadge kind={current.kind} />
                  <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
                    {KIND[current.kind].lead} · {sourceOf(current)}
                  </span>
                </div>

                {current.kind === "question" ? (
                  <QuestionAnswer
                    question={current.question}
                    scale="simple"
                    onAnswered={() => {
                      drop(current, "right");
                      // One answer can close more than its own card — a role places a person, so
                      // their team card goes too — and only the Server's sweep knows which.
                      window.setTimeout(reload, 260);
                    }}
                    onSkip={() => setIndex(position + 1)}
                  />
                ) : current.kind === "speaker" ? (
                  <SpeakerCard
                    speaker={current.speaker}
                    onNamed={() => drop(current, "right")}
                    onSkip={() => setIndex(position + 1)}
                  />
                ) : (
                  <DecidableCard
                    card={current}
                    onAnswered={(direction) => drop(current, direction)}
                    onSkip={() => setIndex(position + 1)}
                  />
                )}
              </article>
            )
          )}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ what each kind is called

/**
 * The icon each kind wears, over the words both reduced surfaces share.
 *
 * Only the icon is decided here: the label and the lead line are copy, and copy that says
 * "Who is this?" rather than "speaker identification" is the feature, so it lives once in
 * `lib/needsYou.ts` where the phone card reads it too.
 */
const ICON: Record<NeedsYouKind, LucideIcon> = {
  correction: SpellCheck,
  speaker: CircleUser,
  insight: Sparkles,
  contradiction: GitFork,
  question: CircleHelp,
};

const KIND = NEEDS_YOU_COPY;

/** Where a card came from, in a reader's words rather than a row's. */
function sourceOf(card: NeedsYouCard): string {
  switch (card.kind) {
    case "correction":
      return withDate(card.correction.sourceTitle, card.correction.sourceDate);
    case "speaker":
      return withDate(card.speaker.sourceTitle, card.speaker.sourceDate);
    case "insight":
      return withDate(card.insight.sourceTitle, card.insight.sourceDate);
    case "contradiction":
      return card.contradiction.subjectTitle;
    case "question":
      return card.question.sourceTitle ?? "noticed in your world view";
  }
}

/** The one line a row in "See all" shows — what that card is actually about. */
function summaryOf(card: NeedsYouCard): string {
  switch (card.kind) {
    case "correction":
      return card.correction.correctedText;
    case "speaker":
      return card.speaker.sampleText ?? "A voice needs a name";
    case "insight":
      return card.insight.normalizedText;
    case "contradiction":
      return card.contradiction.subjectTitle;
    case "question":
      return card.question.question;
  }
}

function KindBadge({ kind }: { kind: NeedsYouKind }) {
  const meta = KIND[kind];
  const Icon = ICON[kind];

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-0.5 text-[length:var(--text-2xs)] font-semibold"
      style={{ background: `color-mix(in oklch, ${meta.colour} 18%, transparent)`, color: meta.colour }}
    >
      <Icon size={11} strokeWidth={2.5} aria-hidden />
      {meta.label}
    </span>
  );
}

function QueueRow({ card, current, onOpen }: { card: NeedsYouCard; current: boolean; onOpen: () => void }) {
  const Icon = ICON[card.kind];

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex w-full items-center gap-[var(--space-3)] border-b border-[var(--border-subtle)] px-[var(--space-4)] py-3 text-left transition last:border-b-0 hover:bg-[var(--surface-2)]",
        current && "bg-[var(--surface-2)]",
      )}
    >
      <Icon size={14} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[length:var(--text-sm)] text-[var(--text-primary)]">{summaryOf(card)}</span>
        <span className="truncate text-[length:var(--text-2xs)] text-[var(--text-muted)]">{sourceOf(card)}</span>
      </span>
      <ChevronRight size={14} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" aria-hidden />
    </button>
  );
}

/**
 * The one state that had to be designed rather than left to fall out of an empty list.
 *
 * "Nothing needs you" is an achievement, not a blank screen, and the sentence under it
 * answers the question somebody actually has at that moment — where did everything I added
 * go? — with the place to go and look.
 */
function AllClear() {
  return (
    <div className="flex flex-col items-center gap-3 px-[var(--space-6)] py-[var(--space-12)] text-center text-[var(--text-muted)]">
      <CheckCheck size={32} strokeWidth={2} className="text-[var(--vl-life)]" aria-hidden />
      <p className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">Nothing needs you</p>
      <p className="max-w-[44ch] text-[length:var(--text-sm)] [text-wrap:pretty]">
        Everything you added is in the world view.
      </p>
      <Link
        to="/world-view"
        className="mt-1.5 inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-2)]"
      >
        <BookOpenText size={13} strokeWidth={2} />
        Open the world view
      </Link>
    </div>
  );
}

// ------------------------------------------------------------------ the three answerable kinds

/**
 * An insight, a wording fix or a belief pair: three kinds whose answer is a button in the
 * footer, which is what separates them from the speaker card (whose answer is *which
 * person*, and which therefore owns its own footer).
 */
function DecidableCard({
  card,
  onAnswered,
  onSkip,
}: {
  card: Exclude<NeedsYouCard, { kind: "speaker" } | { kind: "question" }>;
  onAnswered: (direction: "left" | "right") => void;
  onSkip: () => void;
}) {
  const { api, refreshWorldViewActivity } = useApp();

  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Every answer goes through here, so none of them can forget the guard or the error. */
  function run(work: () => Promise<unknown>, direction: "left" | "right") {
    if (busy) return;
    setBusy(true);
    setProblem(null);

    void work()
      .then(() => onAnswered(direction))
      .catch((cause: unknown) => {
        setProblem(
          cause instanceof ApiError && cause.status === 404
            ? "That has already been answered somewhere else."
            : "That could not be saved. Try again.",
        );
        setBusy(false);
      });
  }

  const answers = answersFor(card, api, refreshWorldViewActivity);

  return (
    <>
      {card.kind === "insight" && <InsightBody insight={card.insight} />}
      {card.kind === "correction" && <CorrectionBody correction={card.correction} />}
      {card.kind === "contradiction" && (
        <ContradictionBody
          card={card.contradiction}
          busy={busy}
          onKeep={(beliefId) =>
            run(() => api.resolveContradiction(card.contradiction.id, "keep", beliefId), "right")
          }
        />
      )}

      {problem && (
        <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {/*
        The footer the handoff draws: skip on the left, then the two answers.

        A belief pair has no separate skip. Its own second answer is already "Leave for
        now", which is what skipping *is* — and the same action twice, once on each side of
        one card, is how somebody presses the wrong one. The phone card made the same call
        in M12.
      */}
      <div className="flex items-center gap-2 border-t border-[var(--border-subtle)] pt-[var(--space-2)]">
        <button
          type="button"
          onClick={onSkip}
          className="inline-flex items-center gap-1.5 text-[length:var(--text-xs)] text-[var(--text-muted)] transition hover:text-[var(--text-secondary)]"
        >
          <ChevronsRight size={12} strokeWidth={2} />
          {card.kind === "contradiction" ? "Leave for now" : "Skip for now"}
        </button>

        <span className="flex-1" />

        {answers.secondary && (
          <button
            type="button"
            disabled={busy}
            onClick={() => run(answers.secondary!.run, "left")}
            className="rounded-[var(--radius-md)] px-3 py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--text-secondary)] transition hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            {answers.secondary.label}
          </button>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => run(answers.primary.run, "right")}
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--primary)] px-[var(--space-4)] py-1.5 text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          <Check size={14} strokeWidth={2.5} />
          {busy ? "Saving…" : answers.primary.label}
        </button>
      </div>
    </>
  );
}

interface Answer {
  label: string;
  run: () => Promise<unknown>;
}

/**
 * What the two buttons say and what they call — the handoff's copy over full mode's calls.
 *
 * The labels are the reason this table exists rather than a `verdict` word per kind:
 * "Approve", "Discard" and "Acknowledge" are the system talking to itself, and the whole
 * point of this surface is that its reader never has to learn that language.
 */
function answersFor(
  card: Exclude<NeedsYouCard, { kind: "speaker" } | { kind: "question" }>,
  api: ReturnType<typeof useApp>["api"],
  refreshWorldViewActivity: () => void,
): { primary: Answer; secondary: Answer | null } {
  switch (card.kind) {
    case "insight":
      return {
        primary: {
          label: "Add to world view",
          run: async () => {
            // UI-23 — start polling the world view's activity right away when this is
            // the answer that started (or is about to start) a pass.
            const { synthesizing } = await api.approveInsight(card.insight.id);
            if (synthesizing) refreshWorldViewActivity();
          },
        },
        secondary: { label: "Not true", run: () => api.discardInsight(card.insight.id) },
      };
    case "correction":
      return {
        primary: {
          label: "Apply the fix",
          run: () => api.resolveProposal(card.correction.sourceMaterialId, card.correction.id, true),
        },
        secondary: {
          label: "Keep the original",
          run: () => api.resolveProposal(card.correction.sourceMaterialId, card.correction.id, false),
        },
      };
    case "contradiction":
      return {
        primary: {
          label: "Both are true",
          run: () => api.resolveContradiction(card.contradiction.id, "acknowledge"),
        },
        secondary: null,
      };
  }
}

function InsightBody({ insight }: { insight: ReviewInsight }) {
  return (
    <>
      <p className="text-[19px] leading-[1.65] text-[var(--text-primary)] [text-wrap:pretty]">
        {insight.normalizedText}
      </p>

      {/* INS-1, said in this surface's own words. Full mode draws the same warning at the
          same moment, and it is the one thing on the card that bears on whether "Add to
          world view" is the right answer — so it is not machinery and is not hidden. */}
      {!insight.grounded && (
        <p className="flex items-start gap-1.5 rounded-[var(--radius-md)] border border-[var(--warning)]/40 bg-[var(--warning)]/10 px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-secondary)] [text-wrap:pretty]">
          <AlertTriangle size={13} strokeWidth={2} className="mt-0.5 shrink-0 text-[var(--warning)]" aria-hidden />
          <span>
            {describeUngrounded(insight.ungroundedReasons) ||
              "Something this sentence leans on could not be named from the source."}{" "}
            Adding it anyway puts a belief about nobody in particular in your world view.
          </span>
        </p>
      )}
    </>
  );
}

/** SPCH-8's diff: what was heard, what it should say, and why. */
function CorrectionBody({ correction }: { correction: PendingCorrection }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[length:var(--text-md)] text-[var(--text-muted)] line-through">{correction.originalText}</p>
      <p className="text-[19px] leading-[1.6] text-[var(--vl-life-soft)] [text-wrap:pretty]">
        {correction.correctedText}
      </p>
      {correction.reasoning && (
        <p className="text-[length:var(--text-xs)] italic text-[var(--text-muted)] [text-wrap:pretty]">
          {correction.reasoning}
        </p>
      )}
    </div>
  );
}

/**
 * AUTO-1 in simple mode. The two beliefs are drawn identically and in full, exactly as they
 * are in the queue and on the phone — the one thing that must not be lost in the narrowing
 * is that the screen does not imply which of them is right.
 *
 * Choosing a side runs the same resolve full mode runs, so the losing belief's window
 * closes and points at the winner exactly as if synthesis had decided it (WORLD-1).
 */
function ContradictionBody({
  card,
  busy,
  onKeep,
}: {
  card: Contradiction;
  busy: boolean;
  onKeep: (beliefId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      <p className="text-[length:var(--text-xl)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
        {card.subjectTitle}
      </p>

      <div className="grid gap-[var(--space-3)] sm:grid-cols-2">
        {[card.beliefA, card.beliefB].map((belief) => (
          <div
            key={belief.id}
            className="flex min-w-0 flex-col gap-2.5 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-2)] p-[var(--space-4)]"
          >
            <p className="text-[length:var(--text-md)] leading-[1.6] text-[var(--text-primary)] [text-wrap:pretty]">
              {belief.statement}
            </p>
            <p className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              {withDate(belief.sourceTitle, belief.sourceDate)}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => onKeep(belief.id)}
              className="mt-auto inline-flex w-fit items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-1.5 text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-3)] disabled:opacity-50"
            >
              <Check size={13} strokeWidth={2.5} />
              Keep this one
            </button>
          </div>
        ))}
      </div>

      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)] [text-wrap:pretty]">
        Keeping both leaves both standing — some disagreements are real in the world.
      </p>
    </div>
  );
}
